import React, { useContext, useState } from 'react';
import { CloudUploadOutlined, DownloadOutlined } from '@ant-design/icons';
import {
  Button,
  notification,
  Space,
  theme as antTheme,
  Typography,
} from 'antd';
import dayjs from 'dayjs';

import DocumentsContext from '@totem/components/documents/report/documentsContext';
import RepositoryTable from '@totem/components/documents/report/repositoryTable';
import {
  creatorLabel,
  expiryState,
  fileTypeLabel,
} from '@totem/components/documents/utilities';
import FiltersChipsContainer from '@totem/components/ui_components/filters/FilterChipsContainer';
import FilterChips from '@totem/components/ui_components/reportSettings/filterChips';
import RecordCountBadge from '@totem/components/ui_components/widgets/RecordCountBadge';
import { exportRecordsToCsv } from '@totem/utilities/fileUtilities';

import './documents.css';

const formatStorage = (bytes: number) =>
  bytes >= 1073741824
    ? `${(bytes / 1073741824).toFixed(1)} GB`
    : `${(bytes / 1048576).toFixed(1)} MB`;

export const DocumentsExtra = () => {
  const { totalRecords } = useContext(DocumentsContext);

  return <RecordCountBadge count={totalRecords ?? 0} />;
};

const Overview = () => {
  const { summary } = useContext(DocumentsContext);
  const { token } = antTheme.useToken();
  if (!summary) {
    return null;
  }
  const files = summary.totalDocuments - summary.webLinks;
  const tiles = [
    {
      label: 'Total documents',
      value: summary.totalDocuments,
      detail: `${files} files · ${summary.webLinks} web links`,
    },
    {
      label: 'Storage used',
      value: formatStorage(summary.totalSize),
      detail: `Across ${files} uploaded files`,
    },
    {
      label: 'Added last 30 days',
      value: summary.addedLast30Days,
      detail: 'New uploads and links',
    },
    {
      label: 'Expiring in 90 days',
      value: summary.expiringNext90Days,
      detail: 'Removed after expiry',
    },
  ];
  const references = summary.byReference ?? [];
  const largest = Math.max(1, ...references.map((entry) => entry.count));
  const panel = {
    border: `1px solid ${token.colorBorder}`,
    borderRadius: token.borderRadiusLG,
    background: token.colorBgContainer,
  };

  return (
    <div styleName="overview">
      <div styleName="kpis">
        {tiles.map((tile) => (
          <div key={tile.label} styleName="kpi" style={panel}>
            <div
              styleName="kpi-label"
              style={{ color: token.colorTextSecondary }}
            >
              {tile.label}
            </div>
            <div styleName="kpi-value">{tile.value}</div>
            <div
              styleName="kpi-detail"
              style={{ color: token.colorTextSecondary }}
            >
              {tile.detail}
            </div>
          </div>
        ))}
      </div>
      <div styleName="references" style={panel}>
        <Typography.Text strong>Documents by reference</Typography.Text>
        {references.map((entry) => (
          <div key={entry.name} styleName="bar">
            <span>{entry.name}</span>
            <span
              styleName="bar-track"
              style={{ background: token.colorFillSecondary }}
            >
              <i
                style={{
                  width: `${(entry.count / largest) * 100}%`,
                  background: token.colorPrimary,
                }}
              />
            </span>
            <strong>{entry.count}</strong>
          </div>
        ))}
      </div>
    </div>
  );
};

const exportHeaders = [
  'Name',
  'Version',
  'Type',
  'Size (bytes)',
  'Linked to',
  'Reference type',
  'Region',
  'Building',
  'Added',
  'Added by',
  'Expires',
  'Expiry',
];

const DocumentsLayout = () => {
  const { data, totalRecords, onAction, action, fetchAllDocuments } =
    useContext(DocumentsContext);
  const [exporting, setExporting] = useState<boolean>(false);

  const exportCsv = () => {
    setExporting(true);

    fetchAllDocuments()
      .then((records) =>
        exportRecordsToCsv(
          'documents',
          exportHeaders,
          records.map(({ document, region, building }) => [
            document.displayName,
            document.version,
            fileTypeLabel(document.fileType),
            document.fileType === 'Link' ? '' : document.size,
            document.objectDisplay,
            document.objectType && document.objectType !== 'None'
              ? document.objectType
              : 'Organization only',
            region.name,
            building.name,
            dayjs(document.createdAt).format('YYYY-MM-DD HH:mm'),
            creatorLabel(document.createdBy),
            dayjs(document.expirationDate).format('YYYY-MM-DD'),
            expiryState(document.expirationDate),
          ]),
        ),
      )
      .catch(() =>
        notification.error({ message: 'Failed to export documents.' }),
      )
      .finally(() => setExporting(false));
  };

  return (
    <>
      <Overview />
      <div styleName="toolbar">
        <Typography.Text type="secondary">
          {(data ?? []).length} of {totalRecords ?? 0} documents on this page
        </Typography.Text>
        <Space>
          <Button
            icon={<DownloadOutlined />}
            loading={exporting}
            disabled={!totalRecords}
            onClick={exportCsv}
          >
            Export CSV
          </Button>
          <Button
            type="primary"
            icon={<CloudUploadOutlined />}
            disabled={action === 'upload' || action === 'link'}
            onClick={() => onAction('upload', null)}
          >
            Upload document
          </Button>
        </Space>
      </div>
      <FiltersChipsContainer>
        <FilterChips />
      </FiltersChipsContainer>
      <RepositoryTable />
      <Typography.Paragraph type="secondary" style={{ fontSize: 11 }}>
        Search and filter from the column headers. Documents are removed
        automatically after their expiry date.
      </Typography.Paragraph>
    </>
  );
};

export default DocumentsLayout;
